import { useCallback } from "react"
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "../components/ui/resizable"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"

export function ResizableRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()

  const children = element.children ?? []
  const panels = children.filter((c) => c.type !== "separator")
  // JITRULE Task 2: the found separator/handle child may nest its fields
  // (ariaLabel, etc.) under `props`.
  const rawHandle = children.find((c) => c.type === "separator")
  const handle = rawHandle ? liftV4Props(rawHandle) : undefined

  // Props-first: check explicit variant/direction before falling back to className.
  // className fallback kept because Cogo JSON may encode direction only as a Tailwind class.
  const isVertical =
    element.variant === "vertical" ||
    (element as unknown as Record<string, unknown>).direction === "vertical" || // TS2352 bridge — reading legacy direction prop not in CogoElement contract
    element.className?.includes("flex-col")

  const handleResize = useCallback(
    (layout: number[]) => {
      const panelSizes = layout
      if (handle && panelSizes.length >= 2) {
        dispatchEvent(handle.id, "drag", { ratio: panelSizes[0] / 100 })
      }
    },
    [handle, dispatchEvent]
  )

  if (panels.length < 2) {
    return (
      <div className={element.className} data-testid={`resizable-${element.id}`}>
        {children.map((c) => (
          <ElementRenderer key={c.id} element={c} />
        ))}
      </div>
    )
  }

  return (
    <ResizablePanelGroup
      direction={isVertical ? "vertical" : "horizontal"}
      className={cn(element.className)}
      data-testid={`resizable-${element.id}`}
      onLayout={handleResize}
      aria-label={element.ariaLabel ?? "Resizable panels"}
    >
      <ResizablePanel defaultSize={50} className="overflow-hidden">
        <ElementRenderer element={panels[0]} />
      </ResizablePanel>
      <ResizableHandle
        withHandle
        aria-label={handle?.ariaLabel ?? "Resize handle"}
      />
      <ResizablePanel defaultSize={50} className="overflow-hidden">
        <ElementRenderer element={panels[1]} />
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}
